/**
 * Where a NEW vault item is saved: your own vault, or a team's.
 *
 * Extracted from `VaultItemModal`'s JSX so the rules below are testable without
 * rendering the modal — they are the part that can go wrong quietly.
 *
 * THREE RULES, each with a reason:
 *
 * 1. THE PICKER IS HIDDEN ENTIRELY WHEN THERE IS NOWHERE ELSE TO SAVE.
 *    A user in no team, or in teams they cannot write to, sees exactly the
 *    modal they saw before this feature existed — not a disabled control, not a
 *    one-option dropdown. The solo experience is the common case and it must be
 *    unchanged, so "no destinations" is represented as an EMPTY LIST rather
 *    than as a list containing only "My vault".
 *
 * 2. A DESTINATION IS ONLY OFFERED WHERE THE SERVER WILL ACTUALLY ACCEPT A
 *    WRITE. Offering a team the user can only read means they fill in an entire
 *    credential form and are refused at save. `can_write` is resolved by the
 *    server per folder and per root; the client never infers it from team role.
 *
 * 3. DESTINATION IS A CREATE-TIME CHOICE ONLY. Moving an existing item between
 *    the personal and a team vault is a copy across two different encryption
 *    models — the personal vault is zero-knowledge for item bodies, the team
 *    vault is server-readable — and is deliberately out of scope. An edit keeps
 *    whatever vault the item is already in.
 */
import { selectCanWriteSomewhere, selectTeamEntry, selectWritableFolders } from '../store/teamVaultStore';

/** Picker value for the user's own vault. */
export const PERSONAL_DESTINATION = 'personal';

/** Picker value for a team. Distinct namespace so it cannot collide. */
export const teamDestinationValue = (teamId) => `team:${teamId}`;

/** `{ kind: 'personal' }` or `{ kind: 'team', teamId }`. */
export function parseDestination(value) {
  if (typeof value !== 'string' || value === PERSONAL_DESTINATION || value === '') {
    return { kind: 'personal', teamId: null };
  }
  if (value.startsWith('team:')) {
    const teamId = value.slice('team:'.length);
    if (teamId) return { kind: 'team', teamId };
  }
  return { kind: 'personal', teamId: null };
}

/**
 * The destination list for the New Item modal.
 *
 * Empty when the user has no team they may write to — see rule 1. Otherwise
 * "My vault" first, then one entry per writable team in the order given.
 *
 * Returns structured entries, not labels: "My vault" is translated at render
 * and a team's label is its own name, which must never be run through i18n.
 */
export function buildDestinations({ teams, byTeam }) {
  const list = Array.isArray(teams) ? teams : [];
  const writableTeams = list.filter(
    (team) => team && team.id != null && selectCanWriteSomewhere(byTeam, team.id)
  );
  if (writableTeams.length === 0) return [];
  return [
    { value: PERSONAL_DESTINATION, kind: 'personal', teamId: null, teamName: null },
    ...writableTeams.map((team) => ({
      value: teamDestinationValue(team.id),
      kind: 'team',
      teamId: String(team.id),
      teamName: team.name || '',
    })),
  ];
}

/**
 * Does the modal render a destination control at all?
 *
 * The single question the JSX asks. Kept as a named function rather than an
 * inline `length > 0` so the "hidden entirely" rule has one definition and one
 * test, instead of being re-decided at each call site.
 */
export function shouldShowDestinations(destinations) {
  return Array.isArray(destinations) && destinations.length > 0;
}

/**
 * Folder options for the chosen destination.
 *
 * The two vaults have genuinely different folder models and this is where that
 * difference is contained:
 *
 *   - PERSONAL folders are a name inside the encrypted item body, lifted into a
 *     device-local plaintext index. There is no registry: a folder exists as
 *     long as something is in it, and the option VALUE is the name itself.
 *     "Create new folder…" is therefore free, and stays exactly as built.
 *
 *   - TEAM folders are server rows with ids and their own per-folder grants.
 *     The value is the folder ID, only folders the user may WRITE to are
 *     listed, and the team root is offered only when root itself is writable.
 *     No inline creation: a team folder is a permission boundary other people
 *     are granted against, so it is created in team settings, not as a side
 *     effect of saving a credential.
 */
export function buildFolderOptions(destination, { personalFolders, byTeam }) {
  const dest = parseDestination(destination);
  if (dest.kind === 'personal') {
    return (Array.isArray(personalFolders) ? personalFolders : []).map((name) => ({
      value: name,
      label: name,
    }));
  }
  return selectWritableFolders(byTeam, dest.teamId).map((f) => ({
    value: String(f.id),
    label: f.name,
  }));
}

/** May the user save to the team ROOT (the "no folder" option for a team)? */
export function canWriteTeamRoot(destination, byTeam) {
  const dest = parseDestination(destination);
  if (dest.kind !== 'team') return true;
  return !!selectTeamEntry(byTeam, dest.teamId).root?.can_write;
}

/**
 * Is this destination one the user may actually save to?
 *
 * Rule 2, asked as a question rather than re-derived. The personal vault is
 * always yes — it is the user's own and is never in `destinations` when there
 * is nothing else to choose between (rule 1). A team is yes only when
 * `buildDestinations` offered it, so there is ONE notion of team writability in
 * the app and every caller consults it instead of reading `can_write` itself.
 *
 * Used by the sidebar's quick-create "+" buttons (enabled/disabled) and by the
 * modal (a pre-selected destination it is handed is ignored unless offered), so
 * the two can never disagree about what the server would accept.
 */
export function isOfferedDestination(destinations, value) {
  if (parseDestination(value).kind === 'personal') return true;
  return (Array.isArray(destinations) ? destinations : []).some((d) => d?.value === value);
}

/**
 * May the user save into THIS team folder? `folderId` null/'' means the root.
 *
 * The per-folder counterpart of the above. A member can hold a grant on one
 * folder and none on the root or its siblings, so "can write to the team" is
 * not the same question as "can write here" — and a quick-create "+" sits on a
 * specific folder row, so it has to ask the specific one.
 */
export function canWriteTeamFolder(byTeam, teamId, folderId) {
  if (folderId == null || folderId === '') {
    return !!selectTeamEntry(byTeam, teamId).root?.can_write;
  }
  return selectWritableFolders(byTeam, teamId).some((f) => String(f.id) === String(folderId));
}

/**
 * The folder a NEW team item should START in.
 *
 * The root when the user may write to it — that is the familiar "no folder"
 * default. Otherwise the first folder they CAN write to, because '' would be a
 * value the picker does not even offer (the root option is omitted when the
 * root is unwritable) and which the server refuses at save. A member with a
 * grant on exactly one folder is the common case for this, not an exotic one.
 */
export function defaultTeamFolderValue(destination, byTeam) {
  if (canWriteTeamRoot(destination, byTeam)) return '';
  const options = buildFolderOptions(destination, { personalFolders: [], byTeam });
  return options.length > 0 ? options[0].value : '';
}

/**
 * The save route for a chosen destination + folder.
 *
 * `folderId` is normalised to `null` for "team root", which is what the API
 * means by a null `folder_id` — a real place that holds items, not "no folder".
 */
export function saveTargetFor(destination, folderValue) {
  const dest = parseDestination(destination);
  if (dest.kind === 'personal') return { kind: 'personal' };
  const folderId = folderValue === '' || folderValue == null ? null : folderValue;
  return { kind: 'team', teamId: dest.teamId, folderId };
}
